import { useCallback, type FC } from "react";
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { message, Tooltip } from "antd";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Button, FileDropzone } from "../../../shared";
import type { FileRejectReason } from "../../../shared/FileDropzone/FileDropzone";

import styles from "./Contract.module.css";

const CONTRACT_ACCEPT = ".pdf,application/pdf";
const CONTRACT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB — matches the toast copy.

export type ContractProps = {
  file: File | null;
  onChange: (file: File | null) => void;
  onSubmit: () => void;
  submitting?: boolean;
};

const Contract: FC<ContractProps> = ({
  file,
  onChange,
  onSubmit,
  submitting = false,
}) => {
  const handleReject = useCallback((_file: File, reason: FileRejectReason) => {
    message.error(
      reason === "size"
        ? AppStrings.FILE_TOO_LARGE
        : AppStrings.ONLY_PDF_FILES_ARE,
    );
  }, []);

  const handleDownload = useCallback(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Firefox cancels the download if we revoke synchronously — defer one tick
    // so the browser has handed the blob off to the download manager.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [file]);

  return (
    <section
      className={styles.container}
      data-testid="new-affiliate-contract"
      aria-labelledby="new-affiliate-contract-title"
    >
      <div className={styles.titleRow}>
        <h2 id="new-affiliate-contract-title" className={styles.title}>
          {AppStrings.AFFILIATE_CONTRACT}
        </h2>
        <span className={styles.optional}>({AppStrings.OPTIONAL})</span>
        <Tooltip title={AppStrings.UPLOAD_AN_OPTIONAL_CONTRACT}>
          <InfoCircleOutlined
            className={styles.titleInfoIcon}
            aria-label={AppStrings.ABOUT_CONTRACT}
            data-testid="new-affiliate-contract-info"
          />
        </Tooltip>
      </div>

      <FileDropzone
        value={file}
        onChange={onChange}
        onReject={handleReject}
        accept={CONTRACT_ACCEPT}
        maxBytes={CONTRACT_MAX_BYTES}
        heading={AppStrings.DRAG_AND_DROP}
        hint={AppStrings.DRAG_AND_DROP_A_FILE}
        buttonLabel={AppStrings.UPLOAD_FILE}
        inputAriaLabel={AppStrings.CONTRACT_FILE_INPUT}
        removeAriaLabel={AppStrings.REMOVE_FILE}
        data-testid="new-affiliate-contract-dropzone"
      />

      <div className={styles.divider} role="separator" aria-hidden="true" />

      <div className={styles.footer}>
        {file ? (
          <Button
            intent="neutral"
            variant="outline"
            iconLeft={<DownloadOutlined />}
            onClick={handleDownload}
            data-testid="new-affiliate-contract-download"
          >
            {AppStrings.DOWNLOAD}
          </Button>
        ) : (
          <span />
        )}
        <Button
          intent="primary"
          onClick={onSubmit}
          loading={submitting}
          disabled={submitting}
          data-testid="new-affiliate-contract-submit"
        >
          {AppStrings.ADD_AFFILIATE}
        </Button>
      </div>
    </section>
  );
};

export default Contract;
